/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { AgenciesService } from '@services/requests';
import { DataStateError } from '@/shared/ui';
import { BlockMid, SettingsBlock } from './blocks';
import { buildPermissionMatrix } from '../utils/build-permission-matrix';
import type { PermissionCategory } from '../constants/permissions-matrix';
import { PermissionsLegend, PermissionCategoryAccordion } from './PermissionsSection.parts';

/**
 * Renders the agency role-permission matrix with expandable categories.
 *
 * @returns The permissions settings section element.
 */
export function PermissionsSection() {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [data, setData] = useState<PermissionCategory[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    if (!agency.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await AgenciesService.getAgencyPermissions(agency.id);
      setData(buildPermissionMatrix(response, t));
    } catch {
      setError(t('v2.permissions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [agency.id, t]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const toggle = (category: string) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  if (loading) {
    return (
      <div data-testid="permissions-loading" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}> {[1, 2, 3, 4, 5].map((i) => ( <div key={i} style={{ height: 48, background:'var(--surface-secondary)',
              borderRadius: 'var(--radius-lg)',
              animation: 'pulse 1.5s infinite',
            }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="permissions-error"
        style={{
          background: 'var(--surface-primary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-8)',
        }}
      >
        <DataStateError
          message={error}
          onRetry={fetchPermissions}
          data-testid="permissions-error-visual"
        />
      </div>
    );
  }

  if (!data) return null;

  return (
    <SettingsBlock
      title={t('v2.permissions.title')}
      caption={t('v2.permissions.subtitle')}
      note={t('v2.permissions.brokerNote')}
      testId="permissions-block"
    >
      <BlockMid>
        <PermissionsLegend />
        <div className="mt-(--space-4) flex flex-col gap-(--space-3)">
          {data.map((group) => (
            <PermissionCategoryAccordion
              key={group.category}
              group={group}
              isOpen={expanded[group.category] ?? false}
              onToggle={toggle}
            />
          ))}
        </div>
      </BlockMid>
    </SettingsBlock>
  );
}
